import { getToken } from 'firebase/app-check';
import { appCheck } from '../firebase';
import { createParser, type EventSourceMessage } from 'eventsource-parser'
import { useEffect, useState } from 'react'

export type ChatMessage = {
  
  id: string
  data: string
  sender: 'user' | 'bot'
  conversationId: string
  fromDB?: boolean // Optional property to indicate if the message is from the database;
}

export type Citation = {
  chunkId: string
  filename: string
  contents: string
  messageId?: string | null
  conversationId?: string
}

function genId() {
  return typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function useOpenStream() {

  const [messages, setMessages]     = useState<ChatMessage[]>([])
  const [citations, setCitations]     = useState<Citation[]>([])
  const [streamOpen, setStreamOpen] = useState<boolean>(false)

  useEffect(() => { 
    // update citations - add messageId to citations if it is null
    if( !citations || citations.length === 0) return
    if( !messages || messages.length === 0) return

    const hasNullMessageId = citations.some((citation) => citation.messageId == null)
    if (hasNullMessageId) {
      const lastMessage = messages.slice().reverse().find((message) => message.sender === 'bot') // find the last bot message
      if(lastMessage){
        setCitations((prevCitations) =>
          prevCitations.map((citation) => {
            if (!citation.messageId) {
          return {
            ...citation,
            messageId: lastMessage ? lastMessage.id : null,
          }
            }
            return citation
          })
        )
      }
    }
  }, [citations])
  
  const openStream = async (conversationId: string, userInternalId: string, userId: string) => {

    console.log("====STARTING STREAM====")

    try {
      
      setStreamOpen(true)

      const { token } = await getToken(appCheck);
      const authToken = localStorage.getItem('authToken')

      const res = await fetch('/openStream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Firebase-Appcheck': token,
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({conversationId, userInternalId, userId}),
      })

      if (!res.ok) {
        throw new Error(await res.text())
      }

      const parser = createParser({
        onEvent(event: EventSourceMessage) {
          
          const msg = JSON.parse(event.data)
          
          if (msg.type === "typing_started" || msg === "ping") return
          
          console.log("====EVENT message====", msg.type , msg)

          if (msg === "ping") {
            console.log("captured ping")
            return
          }
          // handle "bot is done typing" msg and close the stream + set streamOpen = false
          if (msg.type === "typing_stopped") {
            console.log("typing stopped - setting streamOpen to false")
            setStreamOpen(false)
            return
          }

          // if it's a bot message, we add it to the message list
          if (msg.type === "message_created") {
            console.log("=== message created ===", msg)
            
            if (msg.data?.userId === userInternalId) {
              console.log("=== bot sent back user message? idk, it happens ===", msg)
            }
            else {
              const botText = msg.data?.payload?.text ?? msg.text ?? ''
              setMessages((m) => [
                ...m,
                { id: msg.id ?? genId(), data: botText, sender: 'bot', conversationId: msg.data?.conversationId, userId: msg.data?.userId },
              ])
            }
          }
          
          // need to grab all the citations data and display it in it's own UX
          if (msg.type === "custom") {
            console.log("=== custom event ===", msg)
           
            if (msg.data?.event?.citationsInfo && msg.data?.event?.citationsInfo.length > 0) {
              console.log("=== citations info ===", msg.data?.event?.citationsInfo)
              
              const citations = msg.data?.event?.citationsInfo

              citations.forEach( (citation: any) => {
                console.log("=== citation found! ===", citation)
                console.log("=== citation filename ===", citation.metadata?.fileName)
                console.log("=== citation contents ===", citation.content)

                try {
                  const match = citation.metadata?.fileName.match(/[^/]+$/);
                  const filename = match ? match[0] : '';
                  setCitations((c) => [
                    ...c,
                    { chunkId: citation.chunkId, filename: filename, contents: citation.content, conversationId: msg.data?.event?.conversationId, messageId: msg.id || null, sender: 'bot' },
                  ])
                } catch (e) {
                  console.log("=== error parsing citation ===", citation)
                }

              })
            }
          }
        },
      })


      const reader = res.body!.getReader()
      const decoder = new TextDecoder()
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        parser.feed(decoder.decode(value, { stream: true }))
      }

    
    } catch (err) {
      console.error('Stream error', err)
      setStreamOpen(false)
    } finally {

      // TODO: Handle case where bot ends stream before a response is generated
      //        One idea is to just retrigger the question, another is to inform the user of an error and ask to resubmit
      console.log("====ENDING STREAM====")
      setStreamOpen(false)
    }

  }

  return { openStream, messages, setMessages, streamOpen, citations }
}
